import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

/**
 * Tab component - Minimal React wrapper for behavioral concerns.
 *
 * All visual styling is applied via CSS classes. This wrapper provides:
 * - `type="button"` default (prevents form submission bugs)
 * - `data-component="Tab"` for component identification
 * - Ref forwarding for HOC/tooltip compatibility
 * - `className` merging via `cn()`
 *
 * Supports both manual `.tab-active` class usage and Radix `data-state="active"`
 * attribute, so it can be composed with `@radix-ui/react-tabs` via `asChild`
 * or used standalone.
 *
 * The `.tab` base class is applied automatically by this wrapper — you never
 * need to add it manually. Toggle active state via \`data-state="active"\` or
 * the manual \`.tab-active\` class.
 *
 * @example
 * ```tsx
 * // Default tab
 * <Tab>Overview</Tab>
 *
 * // Active via data-state (matches Radix convention)
 * <Tab data-state="active">
 *   <Grid className="tab-icon" />
 *   Overview
 *   <Badge className="tab-badge">3</Badge>
 * </Tab>
 *
 * // Composed with Radix Tabs via asChild
 * <TabsPrimitive.Trigger value="overview" asChild>
 *   <Tab>
 *     <Grid className="tab-icon" />
 *     Overview
 *   </Tab>
 * </TabsPrimitive.Trigger>
 * ```
 */

export type TabProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Tab = React.forwardRef<HTMLButtonElement, TabProps>(
  ({ className, type = 'button', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        data-component="Tab"
        className={cn('tab', className)}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Tab.displayName = 'Tab';
